import {
  Button,
  Stack,
  useTheme,
  useMediaQuery,
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Toolbar,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Select,
  MenuItem,
  LinearProgress,
  SelectChangeEvent,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { environment } from '@env';

import DashboardHeader from '@/@mui/dashboard/DashboardHeader';
import { ApiRequest_UpdatePhone_User, useUpdatePhone } from '@/api/endpoints/user/useUpdatePhone';
import { PropertyManagementRole } from '@/api/enums';
import { ChevronLeftIcon } from '@/assets/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@/assets/icons/ChevronRightIcon';
import { PhoneIcon } from '@/assets/icons/PhoneIcon';
import { PlayIcon } from '@/assets/icons/PlayIcon';
import { SearchMdIcon } from '@/assets/icons/SearchMdIcon';
import { StopIcon } from '@/assets/icons/StopIcon';
import { useAuth } from '@/context/AuthProvider';
import { usePropertyManager } from '@/context/PropertyManagerProvider';
import { getFromNow } from '@/utils/date';
import { formatPhoneNumber } from '@/utils/phone';

import FormTextField from '@/components/form/FormTextField';
import ModalWithForm from '@/components/modals/ModalWithForm';

const TABLE_HEAD = [
  { id: 'caller', label: 'Caller', sortable: false, width: '25%' },
  { id: 'duration', label: 'Duration', sortable: false, width: '10%' },
  { id: 'start_time', label: 'Call Time', sortable: true, width: '15%' },
  { id: 'transcript', label: 'Transcript', sortable: false, width: '40%' },
  { id: 'actions', label: 'Recording', sortable: false, width: '10%' },
];

type Order = 'asc' | 'desc';

interface TInboundCall {
  id: number;
  tenant_id: number;
  from_number: string;
  to_number: string;
  recording_url: string;
  recording_sid: string;
  conference_sid: string;
  recording_channels: string;
  recording_duration: string;
  recording_start_time: string;
  transcript: string | null;
  tenant?: {
    contact?: {
      name: string;
      phone: string;
    };
  };
}

interface PropertyManagerData {
  id: number;
  limitedUser: {
    id: string;
    email: string;
    phone?: string;
    full_name?: string;
  };
}

interface PropertyManagement {
  id: number;
  manager_id: number;
  location_id: number;
  role: PropertyManagementRole;
  manager: PropertyManagerData;
}

interface CallForwardModalProps {
  onClose: () => void;
}

const CallForwardModal: React.FC<CallForwardModalProps> = ({ onClose }) => {
  const { user } = useAuth();
  const [, setFormError] = useState<Error | null>(null);

  const form = useForm<ApiRequest_UpdatePhone_User>({
    defaultValues: {
      phone: user?.phone ?? '',
    },
  });

  const updatePhoneMutation = useUpdatePhone({
    onError: error => {
      setFormError(error);
    },
    onSuccess: () => {
      onClose();
    },
  });

  const onSubmit = useCallback(
    (data: ApiRequest_UpdatePhone_User) => {
      setFormError(null);
      return updatePhoneMutation.mutateAsync(data);
    },
    [updatePhoneMutation]
  );

  return (
    <ModalWithForm
      title="Update Call Forwarding Number"
      form={form}
      maxWidth="sm"
      onClose={onClose}
      onSubmit={onSubmit}
      buttonLabel="Update Phone Number"
    >
      <Stack spacing={2}>
        <Typography variant="body2">
          When an inbound call comes in for your property's phone number, it will be automatically
          forwarded to this number. Please ensure this number is always up to date.
        </Typography>
        <FormTextField
          label="Your Phone Number"
          placeholder="Enter your phone number"
          size="small"
          error={!!form.formState.errors.phone}
          helperText={form.formState.errors.phone?.message?.toString()}
          control={form.control}
          required
          {...form.register('phone', {
            required: 'Phone Number is required',
          })}
        />
      </Stack>
    </ModalWithForm>
  );
};

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  const aValue = a[orderBy];
  const bValue = b[orderBy];

  if (bValue === null || bValue === undefined) return -1;
  if (aValue === null || aValue === undefined) return 1;

  if (typeof aValue === 'string' && typeof bValue === 'string') {
    return bValue.localeCompare(aValue);
  }

  if (bValue < aValue) return -1;
  if (bValue > aValue) return 1;
  return 0;
}

function getComparator<Key extends keyof TInboundCall>(
  order: Order,
  orderBy: Key,
): (a: TInboundCall, b: TInboundCall) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

const InboundCallListPage: React.FC = () => {
  const { session, user } = useAuth();
  const { selectedPropertyLocation } = usePropertyManager();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();

  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof TInboundCall>('recording_start_time');
  const [filterName, setFilterName] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [isCallForwardModalOpen, setIsCallForwardModalOpen] = useState(false);
  const [playingRecordingId, setPlayingRecordingId] = useState<number | null>(null);
  const [expandedTranscript, setExpandedTranscript] = useState<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const fetchCallsQuery = useQuery({
    queryKey: ['GET', 'inbound-calls', session?.access_token, selectedPropertyLocation?.id],
    queryFn: async () => {
      try {
        const response = await axios.get(
          `${environment.api}/twilio/inbound-calls/${selectedPropertyLocation?.id}`,
          {
            headers: {
              Authorization: `Bearer ${session?.access_token}`,
            },
            params: {
              phone: selectedPropertyLocation?.phone,
            },
          }
        );
        return response.data;
      } catch (error) {
        enqueueSnackbar('Failed to fetch inbound calls.', { variant: 'error' });
        return [];
      }
    },
    enabled:
      !!session?.access_token &&
      !!selectedPropertyLocation?.id &&
      !!selectedPropertyLocation?.phone,
  });

  const isAdminQuery = useQuery({
    queryKey: ['GET', 'is-admin', selectedPropertyLocation?.id, user?.id],
    queryFn: async () => {
      try {
        const response = await axios.get(`${environment.api}/property-manager/all`, {
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
          },
          params: {
            location_id: selectedPropertyLocation?.id,
          },
        });
        const propertyManagers = (response.data as PropertyManagement[]) || [];
        const currentUserManagement = propertyManagers.find(
          (pm: PropertyManagement) => pm.manager.limitedUser.id === user?.id
        );
        return currentUserManagement?.role === PropertyManagementRole.ADMIN;
      } catch (error) {
        return false;
      }
    },
    enabled: !!session?.access_token && !!selectedPropertyLocation?.id && !!user?.id,
  });

  const handlePlayRecording = useCallback(
    (recordingUrl: string, callId: number) => {
      if (audioRef.current) {
        if (playingRecordingId === callId) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
          setPlayingRecordingId(null);
        } else {
          audioRef.current.src = recordingUrl;
          audioRef.current.play();
          setPlayingRecordingId(callId);
        }
      }
    },
    [playingRecordingId]
  );

  const handleRequestSort = (property: keyof TInboundCall) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterName(event.target.value);
    setPage(0);
  };

  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>, _: React.ReactNode) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const filteredData = useMemo(() => {
    let filtered = fetchCallsQuery.data || [];

    if (filterName) {
      filtered = filtered.filter((item: TInboundCall) =>
        (item.tenant?.contact?.name || '').toLowerCase().includes(filterName.toLowerCase())
      );
    }

    return [...filtered].sort(getComparator(order, orderBy));
  }, [fetchCallsQuery.data, filterName, order, orderBy]);

  const displayedData = useMemo(
    () => filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredData, page, rowsPerPage]
  );

  return (
    <Stack
      direction="column"
      spacing={2}
      sx={{
        flex: '1 1 100%',
        padding: t => ({
          xs: t.spacing(1),
          sm: t.spacing(2),
          md: t.spacing(3),
        }),
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: { xs: 2, sm: 0 },
          display: 'flex',
          justifyContent: { sm: 'space-between' },
          width: '100%',
        }}
      >
        <DashboardHeader hasInnerHtml title="Calls: <em>Inbound</em>" />
        {isAdminQuery.data && (
          <Button
            variant="contained"
            startIcon={<PhoneIcon />}
            onClick={() => setIsCallForwardModalOpen(true)}
            size={isMobile ? 'small' : 'medium'}
          >
            Call Forwarding
          </Button>
        )}
      </Box>

      <Typography
        variant="body1"
        color="text.secondary"
        sx={{
          mb: 2,
          mt: { xs: 1, sm: 0 },
        }}
      >
        View and listen to recordings of inbound calls from tenants. Any inbound calls to your
        property's phone number will be recorded and available here. All inbound calls are
        automatically forwarded to your phone number.
      </Typography>

      <Card sx={{ bgcolor: 'common.white' }}>
        <Toolbar
          sx={{
            height: 64,
            display: 'flex',
            justifyContent: 'space-between',
            p: theme.spacing(2),
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'common.white',
          }}
        >
          <OutlinedInput
            fullWidth
            value={filterName}
            onChange={handleFilterName}
            placeholder="Search callers..."
            startAdornment={
              <InputAdornment position="start">
                <SearchMdIcon sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            }
            sx={{
              maxWidth: 320,
              bgcolor: 'common.white',
            }}
          />
        </Toolbar>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {TABLE_HEAD.map(headCell => (
                  <TableCell
                    key={headCell.id}
                    align={headCell.id === '' ? 'right' : 'left'}
                    sortDirection={orderBy === headCell.id ? order : false}
                    sx={{
                      width: headCell.width,
                      borderRight: 'none',
                      '&:last-child': {
                        borderRight: 'none',
                      },
                      bgcolor: theme.palette.secondary[200],
                      textTransform: 'none',
                      color: 'text.primary',
                      '& .MuiTableSortLabel-root': {
                        textTransform: 'none',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: { xs: '0.875rem', sm: 'inherit' },
                        fontWeight: 'bold',
                        color: 'text.primary',
                        '&:hover': {
                          color: 'text.primary',
                        },
                        '&.Mui-active': {
                          color: 'text.primary',
                          '&:hover': {
                            color: 'text.primary',
                          },
                        },
                      },
                    }}
                  >
                    {headCell.sortable ? (
                      <TableSortLabel
                        active={orderBy === headCell.id}
                        direction={orderBy === headCell.id ? order : 'asc'}
                        onClick={() => handleRequestSort(headCell.id as keyof TInboundCall)}
                      >
                        {headCell.label}
                      </TableSortLabel>
                    ) : (
                      <Typography
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: { xs: '0.875rem', sm: 'inherit' },
                          fontWeight: 'bold',
                          color: 'text.primary',
                          '&:hover': {
                            color: 'text.primary',
                          },
                        }}
                      >
                        {headCell.label}
                      </Typography>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {(() => {
                if (fetchCallsQuery.isLoading) {
                  return (
                    <TableRow>
                      <TableCell colSpan={TABLE_HEAD.length} align="center" sx={{ py: 3 }}>
                        <LinearProgress sx={{ width: '100%' }} />
                      </TableCell>
                    </TableRow>
                  );
                }

                if (displayedData.length === 0) {
                  return (
                    <TableRow>
                      <TableCell colSpan={TABLE_HEAD.length} align="center" sx={{ py: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          No inbound calls found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                }

                return displayedData.map(row => (
                  <TableRow
                    key={row.id}
                    sx={{
                      '&:hover': {
                        backgroundColor: `${theme.palette.secondary[200]} !important`,
                        cursor: 'pointer',
                        transition: 'background-color 0.2s ease',
                      },
                    }}
                  >
                    <TableCell>
                      <Stack spacing={0.25}>
                        <Typography 
                          variant="body3" 
                          sx={{ 
                            color: theme.palette.secondary.darker,
                            fontWeight: 'bold'
                          }}
                        >
                          {row.tenant?.contact?.name || 'Unknown'}
                        </Typography>
                        <Typography variant="body3" sx={{ color: theme.palette.secondary.darker }}>
                          {formatPhoneNumber(row.from_number)}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body3" sx={{ color: theme.palette.secondary.darker }}>
                        {row.recording_duration} seconds
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body3" sx={{ color: theme.palette.secondary.darker }}>
                        {getFromNow(new Date(row.recording_start_time))}
                      </Typography>
                    </TableCell>
                    <TableCell
                      onClick={() => setExpandedTranscript(prev =>
                        prev === row.id ? null : row.id
                      )}
                      sx={{ cursor: 'pointer' }}
                    >
                      <Typography
                        variant="body3"
                        sx={{
                          color: theme.palette.secondary.darker,
                          display: '-webkit-box',
                          WebkitLineClamp: expandedTranscript === row.id ? 'unset' : 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {row.transcript || 'No transcript available'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton 
                        onClick={() => handlePlayRecording(row.recording_url, row.id)}
                        sx={{ 
                          color: theme.palette.primary.main,
                          '&:hover': {
                            color: theme.palette.primary.main,
                          }
                        }}
                      >
                        {playingRecordingId === row.id ? <StopIcon /> : <PlayIcon />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ));
              })()}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'common.white',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Rows per page:
            </Typography>
            <Select
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              size="small"
              sx={{ width: 80 }}
              inputProps={{ 'aria-label': 'rows per page' }}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {`${page * rowsPerPage + 1}-${Math.min(
                (page + 1) * rowsPerPage,
                filteredData.length
              )} of ${filteredData.length}`}
            </Typography>
            <IconButton
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 0}
              size="small"
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= Math.ceil(filteredData.length / rowsPerPage) - 1}
              size="small"
            >
              <ChevronRightIcon />
            </IconButton>
          </Stack>
        </Box>
      </Card>

      <audio ref={audioRef} style={{ display: 'none' }} controls>
        <track kind="captions" />
      </audio>

      {isCallForwardModalOpen && (
        <CallForwardModal onClose={() => setIsCallForwardModalOpen(false)} />
      )}
    </Stack>
  );
};

export default InboundCallListPage;
